import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
} from "@mui/material";
import {
  School as SchoolIcon,
  Dashboard as DashboardIcon,
  LocationOn as LocationOnIcon,
  AccountCircle as AccountIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSchools, SchoolData } from "../hooks/useSchools";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { AdmissionFromModal } from "../components/Modals/AdmissionFromModal";
import { useApplicationForm } from "../hooks/useApplicationForm";
import { useClasses } from "../hooks/useClasses";
import { useSessions } from "../hooks/useSession";
// Removed useSubjects import - not using it in school selection dashboard
import { setTenantSchema, getStudentData, getApplicationFormDraft, clearApplicationFormDraft } from "../utils/storage";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const SchoolSelectionDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAppUser = user?.roles?.includes('APP_USER');
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();
  // Disable schoolAdminsQuery in school selection dashboard
  const { schoolsQuery } = useSchools(false, { enableQuery: true, disableSchoolAdmins: true });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectSchoolDialogOpen, setSelectSchoolDialogOpen] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | undefined>(undefined);
  const [selectedSchool, setSelectedSchool] = useState<SchoolData | null>(null);
  const [admissionModalOpen, setAdmissionModalOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [schoolDescriptionModalOpen, setSchoolDescriptionModalOpen] = useState(false);
  const [schoolForDescription, setSchoolForDescription] = useState<SchoolData | null>(null);

  // Update application form search states
  const [updateSearchDialogOpen, setUpdateSearchDialogOpen] = useState(false);
  const [searchSchoolId, setSearchSchoolId] = useState<number | undefined>(undefined);
  const [mobileNo, setMobileNo] = useState<string>("");
  const [dob, setDob] = useState<Date | null>(null);
  const [searchError, setSearchError] = useState<string>("");
  const [mobileValidationError, setMobileValidationError] = useState<string>("");
  const [shouldSearch, setShouldSearch] = useState(false);
  const [searchMobileNo, setSearchMobileNo] = useState<string>("");
  const [searchDob, setSearchDob] = useState<string>("");
  const [foundApplication, setFoundApplication] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentFormData, setCurrentFormData] = useState<any>(undefined);
  const [studentName, setStudentName] = useState<string>("");
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [restoreDraftDialogOpen, setRestoreDraftDialogOpen] = useState(false);
  const [draftData, setDraftData] = useState<any | null>(null);

  const isLoading = schoolsQuery.isLoading || schoolsQuery.isFetching;
  const error = schoolsQuery.error ? 'Failed to load schools. Please try again later.' : null;
  const schools = schoolsQuery.data || [];

  // Fetch classes, sessions, and subjects for the selected school
  const { classesQuery } = useClasses(selectedSchool?.id, false);
  const { sessionsQuery } = useSessions(selectedSchool?.id);
  // Disable subjects API call - do not fetch subjects in school selection dashboard
  // Create a mock query object to avoid calling the API
  const subjectsQuery = { data: [], isLoading: false, error: null };

  // Fetch student name on component mount
  useEffect(() => {
    const fetchStudentName = () => {
      const studentData = getStudentData();
      if (studentData) {
        // Student data from login/register
        const name = studentData.studentName || studentData.name || user?.username || "";
        setStudentName(name);
      } else if (user?.username) {
        // Fallback to username if student data not available
        setStudentName(user.username);
      }
    };

    if (user) {
      fetchStudentName();
    }
  }, [user]);

  // Format date to ISO format (yyyy-MM-dd)
  const formatDateToISO = useCallback((date: Date | null): string => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Use application form hook with search parameters
  const {
    createApplicationFormMutation,
    updateApplicationFormMutation,
    applicationFormsByMobileNoAndDOBQuery
  } = useApplicationForm(
    false,
    selectedSchool?.id,
    shouldSearch ? searchMobileNo : undefined,
    shouldSearch ? searchDob : undefined
  );

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/');
  };

  const handleSchoolSelect = (school: SchoolData) => {
    // Open modal to show school description
    setSchoolForDescription(school);
    setSchoolDescriptionModalOpen(true);
  };

  const generateSchoolDescription = (school: SchoolData): string => {
    const parts: string[] = [];

    if (school.nameofSchool) {
      parts.push(`${school.nameofSchool} ${t("school_description_institution")}`);
    } else {
      parts.push(t("school_description_this_institution"));
    }

    const locationParts: string[] = [];
    if (school.district) {
      locationParts.push(school.district);
    }

    if (school.state) {
      if (locationParts.length > 0) {
        locationParts.push(school.state);
      } else {
        locationParts.push(school.state);
      }
    }

    if (locationParts.length > 0) {
      parts.push(` ${t("school_description_located_in")} ${locationParts.join(", ")}`);
    }

    if (school.blockName) {
      parts.push(` ${t("school_description_in_block")} ${school.blockName} ${t("school_description_block")}`);
    }

    parts.push(".");

    if (school.schoolCode) {
      parts.push(` ${t("school_description_recognized_code")} ${school.schoolCode}`);
    }

    if (school.diseCode) {
      if (school.schoolCode) {
        parts.push(` ${t("school_description_and_dise")} ${school.diseCode}`);
      } else {
        parts.push(` ${t("school_description_recognized_code")} ${school.diseCode}`);
      }
    }

    if (school.schoolCode || school.diseCode) {
      parts.push(".");
    }

    if (school.schoolPrincipalName) {
      parts.push(` ${t("school_description_led_by")} ${school.schoolPrincipalName}`);
      if (school.principalDesignation) {
        parts.push(`, ${school.principalDesignation}`);
      }
      parts.push(".");
    }

    if (school.medium && school.medium.length > 0) {
      const mediums = school.medium.join(", ");
      const mediumKey = school.medium.length > 1 ? "school_description_mediums" : "school_description_medium";
      parts.push(` ${t("school_description_offers_education")} ${mediums} ${t(mediumKey)}`);
      parts.push(".");
    }

    if (school.pinNo) {
      parts.push(` ${t("school_description_located_at_pin")} ${school.pinNo}`);
      parts.push(".");
    }

    parts.push(` ${t("school_description_commitment")}`);

    return parts.join("");
  };

  const handleCloseSchoolDescription = () => {
    setSchoolDescriptionModalOpen(false);
    setSchoolForDescription(null);
  };

  const handleProceedWithSchool = () => {
    if (schoolForDescription) {
      handleCloseSchoolDescription();

      // Set tenant schema from selected school
      if (schoolForDescription.tenant?.schemaName) {
        const tenantSchema = schoolForDescription.tenant.schemaName;
        // Set in storage
        setTenantSchema(tenantSchema);
        // Set axios default header so all API calls use this tenant
        axios.defaults.headers.common['X-Tenant-ID'] = tenantSchema;
      }

      setSelectedSchool(schoolForDescription);
      setAdmissionModalOpen(true);
      setSubmitError(null);
      setIsEditMode(false);
      setCurrentFormData(undefined);
    }
  };

  const handleStartApplication = () => {
    setSelectedSchoolId(undefined);
    setSelectSchoolDialogOpen(true);
  };

  const handleOpenAdmissionForm = () => {
    const school = schools.find((s) => s.id === selectedSchoolId);
    if (!school) return;

    // Set tenant schema from selected school
    if (school.tenant?.schemaName) {
      const tenantSchema = school.tenant.schemaName;
      // Set in storage
      setTenantSchema(tenantSchema);
      // Set axios default header so all API calls use this tenant
      axios.defaults.headers.common['X-Tenant-ID'] = tenantSchema;
    }

    setSelectedSchool(school);
    setSelectSchoolDialogOpen(false);
    setSelectedSchool(school);
    setSelectSchoolDialogOpen(false);

    // Check for draft data
    const savedDraft = getApplicationFormDraft();
    if (savedDraft) {
      setDraftData(savedDraft);
      setRestoreDraftDialogOpen(true);
    } else {
      setAdmissionModalOpen(true);
      setSubmitError(null);
      setIsEditMode(false);
      setCurrentFormData(undefined);
    }
  };

  const handleAdmissionSubmit = async (formData: any) => {
    if (!selectedSchool?.id) {
      setSubmitError(t("please_select_school_first") || "Please select a school first.");
      return;
    }

    try {
      setSubmitError(null);

      // Ensure all required fields are present
      const completeFormData = {
        ...formData,
        schoolId: selectedSchool.id,
        // Ensure sessionId is set if sessionName is provided
        sessionId: formData.sessionId || (formData.sessionName ?
          sessionsQuery.data?.find(s => s.name === formData.sessionName)?.id || 0 : 0),
      };


      if (isEditMode && currentFormData?.id) {
        // Capture the updated form data from the mutation response
        const updatedFormData = await updateApplicationFormMutation.mutateAsync({
          id: currentFormData.id,
          data: completeFormData,
        });

        // Update currentFormData with the response from the server
        setCurrentFormData(updatedFormData);

        // Invalidate queries after successful update
        await queryClient.invalidateQueries({
          queryKey: ["applicationFormsByMobileNoAndDOB"]
        });
        await queryClient.invalidateQueries({
          queryKey: ["applicationFormsBySchoolId", selectedSchool.id]
        });
        await queryClient.invalidateQueries({
          queryKey: ["applicationForms"]
        });

        // Invalidate photo queries for the updated form
        await queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              (Array.isArray(queryKey) &&
                queryKey.length >= 2 &&
                (queryKey[0] === "applicationPhotoInfo" || queryKey[0] === "studentPhotoInfo") &&
                queryKey[1] === currentFormData.id) ||
              (Array.isArray(queryKey) &&
                queryKey.length >= 1 &&
                queryKey[0] === "applicationForms" &&
                queryKey.includes(currentFormData.id))
            );
          }
        });

        // Remove cached data for this specific application form
        queryClient.removeQueries({
          queryKey: ["applicationForm", currentFormData.id],
        });
        queryClient.removeQueries({
          queryKey: ["applicationPhotoInfo", currentFormData.id],
        });
        queryClient.removeQueries({
          queryKey: ["studentPhotoInfo", currentFormData.id],
        });

        setSubmitSuccess(t("application_form_updated_successfully") || "Application form updated successfully!");
        // Keep currentFormData updated with the latest response for potential reopening
        // Don't reset it here - it will be reset when modal closes or new search is performed
      } else {
        await createApplicationFormMutation.mutateAsync(completeFormData);
        setSubmitSuccess(t("application_form_submitted_successfully") || "Application form submitted successfully!");
        // Clear draft data after successful submission
        clearApplicationFormDraft();
      }
      setAdmissionModalOpen(false);
      setIsEditMode(false);
      // Only reset currentFormData if not in edit mode (for new forms)
      if (!isEditMode) {
        setCurrentFormData(undefined);
      }
      setFoundApplication(null);

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(null);
      }, 5000);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to submit application.";
      setSubmitError(errorMessage);
    }
  };

  // Handle update application form search
  const handleUpdateApplicationSearch = () => {
    setUpdateSearchDialogOpen(true);
    // Reset search state
    setShouldSearch(false);
    setSearchMobileNo("");
    setSearchDob("");
    setSearchError("");
    setMobileValidationError("");
    setSearchSchoolId(undefined);
    setMobileNo("");
    setDob(null);
    setFoundApplication(null);
  };

  // Handle search for update
  const handleUpdateSearch = useCallback(() => {
    // Clear previous errors
    setSearchError("");
    setMobileValidationError("");

    // Validate school selection
    if (!searchSchoolId) {
      setSearchError(t("please_select_school") || "Please select a school");
      return;
    }

    // Validate mobile number
    const trimmedMobile = mobileNo.trim();
    if (!trimmedMobile) {
      setSearchError(t("please_enter_mobile_number") || "Please enter mobile number");
      return;
    }

    // Mobile number validation: must start with 6,7,8,9 and be exactly 10 digits
    if (!/^[6-9]/.test(trimmedMobile)) {
      setMobileValidationError(t("mobile_number_must_start_with_6789") || "Mobile number must start with 6, 7, 8, or 9");
      return;
    }
    if (!/^\d{10}$/.test(trimmedMobile)) {
      setMobileValidationError(t("mobile_number_must_be_10_digits") || "Mobile number must be exactly 10 digits");
      return;
    }

    if (!dob) {
      setSearchError(t("please_select_date_of_birth") || "Please select date of birth");
      return;
    }

    // Set tenant schema from selected school
    const selectedSchool = schools.find(s => s.id === searchSchoolId);
    if (selectedSchool?.tenant?.schemaName) {
      const tenantSchema = selectedSchool.tenant.schemaName;
      setTenantSchema(tenantSchema);
      axios.defaults.headers.common['X-Tenant-ID'] = tenantSchema;
    }

    setSearchMobileNo(trimmedMobile);
    setSearchDob(formatDateToISO(dob));
    setShouldSearch(true);
  }, [mobileNo, dob, searchSchoolId, schools, formatDateToISO, t]);

  // Handle successful search result for update
  useEffect(() => {
    if (applicationFormsByMobileNoAndDOBQuery.data && shouldSearch) {
      const foundApp = applicationFormsByMobileNoAndDOBQuery.data;
      setFoundApplication(foundApp);

      // Check if application is rejected
      if (foundApp.isRegistered === 'Rejected' || foundApp.isRegistered === 'REJECTED') {
        // Show rejection dialog instead of opening edit modal
        setUpdateSearchDialogOpen(false);
        setRejectionDialogOpen(true);
        setShouldSearch(false);
        setSearchMobileNo("");
        setSearchDob("");
        // Reset search fields
        setMobileNo("");
        setDob(null);
        return;
      }

      // Find the school from the found application
      const appSchoolId = foundApp.schoolId;
      const appSchool = schools.find((s) => s.id === appSchoolId);

      if (appSchool) {
        // Set tenant schema from found school
        if (appSchool.tenant?.schemaName) {
          const tenantSchema = appSchool.tenant.schemaName;
          setTenantSchema(tenantSchema);
          axios.defaults.headers.common['X-Tenant-ID'] = tenantSchema;
        }

        setSelectedSchool(appSchool);
        setCurrentFormData(foundApp);
        setIsEditMode(true);
        setUpdateSearchDialogOpen(false);
        setAdmissionModalOpen(true);
        setSubmitError(null);
      } else {
        setSearchError(t("school_not_found_for_application") || "School not found for this application");
      }

      // Reset search fields and state
      setMobileNo("");
      setDob(null);
      setShouldSearch(false);
      setSearchMobileNo("");
      setSearchDob("");
    }
  }, [applicationFormsByMobileNoAndDOBQuery.data, shouldSearch, schools, t]);

  // Handle search error for update
  useEffect(() => {
    if (applicationFormsByMobileNoAndDOBQuery.error && shouldSearch) {
      setSearchError(t("application_form_not_found") || "Application form not found");
      setShouldSearch(false);
    }
  }, [applicationFormsByMobileNoAndDOBQuery.error, shouldSearch, t]);

  // Memoize initialData to prevent unnecessary re-renders and form resets
  // MUST be placed before any conditional returns to comply with React Hooks rules
  const memoizedInitialData = useMemo(() => {
    // If editing or if we have draft data (currentFormData), use it
    if (currentFormData) {
      return {
        ...currentFormData,
        schoolId: currentFormData.schoolId || selectedSchool?.id || 0,
        // Explicitly include photo and signature from API response
        studentPhoto: currentFormData.studentPhoto || null,
        studentSignature: currentFormData.studentSignature || null,
      };
    }

    // If not editing and no draft, pre-fill with student data from login/register
    const studentData = getStudentData();
    if (user && !isEditMode) {
      // Get student name and mobile number
      const name = studentData?.studentName || studentData?.name || user.username || '';
      const mobile = studentData?.mobileNo || studentData?.mobileNumber || user.email || '';

      // Create fresh initial data object with only studentName and mobileNo
      const initialFormData: any = {
        schoolId: selectedSchool?.id || 0,
      };

      // Only set values if they exist
      if (name) {
        initialFormData.studentName = name;
      }
      if (mobile) {
        initialFormData.mobileNo = mobile;
      }

      return initialFormData;
    }

    // If no user or student data, return with schoolId only
    return {
      schoolId: selectedSchool?.id || 0,
    };
  }, [currentFormData, selectedSchool?.id, user, isEditMode]);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      {/* Header Bar - Using Layout structure */}
      <AppBar
        position="fixed"
        sx={{
          width: "100%",
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafe 100%)",
          color: "text.primary",
          borderBottom: "none",
          boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Toolbar>
          <Box sx={{ padding: "12px 0", flex: 1 }}>
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                background: "#3092e3",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: { xs: "1.5rem", md: "2rem" },
                lineHeight: 1.2,
              }}
            >
              School Selection Dashboard
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.875rem", md: "1rem" },
                fontWeight: 500,
                opacity: 0.8,
              }}
            >
              Select your school to continue
            </Typography>
          </Box>

          {/* Right side - Language selector and User avatar */}
          <Box
            display="flex"
            alignItems="center"
            sx={{
              ml: "auto",
              flexShrink: 0,
            }}
          >
            <FormControl
              size="small"
              sx={{
                minWidth: 150,
                mx: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(50, 140, 249, 0.2)",
                  transition: "all 0.3s ease-in-out",
                  boxShadow: "0 2px 8px rgba(50, 140, 249, 0.1)",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 16px rgba(50, 140, 249, 0.15)",
                    border: "1px solid rgba(50, 140, 249, 0.3)",
                  },
                  "&.Mui-focused": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 16px rgba(50, 140, 249, 0.2)",
                    border: "#3092e3",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#3092e3",
                  fontWeight: 600,
                  "&.Mui-focused": {
                    color: "#3092e3",
                  },
                },
                "& .MuiSelect-select": {
                  fontWeight: 500,
                  color: "#333",
                },
              }}
            >
              <InputLabel> {t("language")}</InputLabel>
              <Select
                value={i18n.language}
                onChange={(e) => {
                  const newLang = e.target.value;
                  i18n.changeLanguage(newLang);
                  try {
                    localStorage.setItem("selectedLanguage", newLang);
                    localStorage.setItem("i18nextLng", newLang);
                  } catch (error) {
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 1,
                      boxShadow: "0 8px 32px rgba(50, 140, 249, 0.15)",
                      border: "1px solid rgba(50, 140, 249, 0.1)",
                      "& .MuiMenuItem-root": {
                        borderRadius: 1,
                        mx: 1,
                        my: 0.5,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: "rgba(50, 140, 249, 0.08)",
                          transform: "translateX(4px)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "rgba(50, 140, 249, 0.15)",
                          color: "#3092e3",
                          fontWeight: 600,
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="hi">हिन्दी</MenuItem>
                <MenuItem value="en">English</MenuItem>
              </Select>
            </FormControl>

            {user && (
              <>
                <IconButton
                  onClick={handleProfileMenuOpen}
                  size="small"
                  sx={{
                    ml: 2,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      background: "#3092e3",
                      boxShadow: "0 4px 12px rgba(1, 104, 234, 0.3)",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                    }}
                  >
                    {user.username.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleProfileMenuClose}
                  onClick={handleProfileMenuClose}
                  PaperProps={{
                    sx: {
                      borderRadius: 3,
                      boxShadow: "0 8px 32px rgba(59, 162, 245, 0.15)",
                      border: "1px solid rgba(59, 162, 245, 0.1)",
                      minWidth: 180,
                      "& .MuiMenuItem-root": {
                        borderRadius: 1,
                        mx: 1,
                        my: 0.5,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: "rgba(59, 162, 245, 0.08)",
                          transform: "translateX(4px)",
                        },
                      },
                    },
                  }}
                >
                  <MenuItem onClick={handleProfileMenuClose}>
                    <ListItemIcon>
                      <AccountIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t("profile_management")}</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(244, 67, 54, 0.08)",
                        "& .MuiListItemText-primary": {
                          color: "#f44336",
                        },
                      },
                    }}
                  >
                    <ListItemText
                      sx={{
                        "& .MuiListItemText-primary": {
                          color: "#f44336",
                          fontWeight: 500,
                        },
                      }}
                    >
                      {t("logout")}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Restore Draft Dialog */}
      <Dialog
        open={restoreDraftDialogOpen}
        onClose={() => {
          // Default to fresh start if dialog is closed without choice
          clearApplicationFormDraft();
          setDraftData(null);
          setRestoreDraftDialogOpen(false);
          setAdmissionModalOpen(true);
          setSubmitError(null);
          setIsEditMode(false);
          setCurrentFormData(undefined);
        }}
        aria-labelledby="restore-draft-dialog-title"
        aria-describedby="restore-draft-dialog-description"
      >
        <DialogTitle id="restore-draft-dialog-title">
          {t('restore_previous_data') || 'Restore Previous Data?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="restore-draft-dialog-description">
            {t('restore_draft_message_application') || 'We found previously filled application data. Would you like to restore it?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              clearApplicationFormDraft();
              setDraftData(null);
              setRestoreDraftDialogOpen(false);
              setAdmissionModalOpen(true);
              setSubmitError(null);
              setIsEditMode(false);
              setCurrentFormData(undefined);
            }}
            variant="outlined"
            sx={{ borderColor: "#666", color: "#666" }}
          >
            {t('no_start_fresh') || 'No, Start Fresh'}
          </Button>
          <Button
            onClick={() => {
              // Use draft data as initial data
              setCurrentFormData(draftData);
              setRestoreDraftDialogOpen(false);
              setAdmissionModalOpen(true);
              setSubmitError(null);
              setIsEditMode(false); // Still new creation, just pre-filled
            }}
            variant="contained"
            autoFocus
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {t('yes_restore') || 'Yes, Restore'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: 8,
          bgcolor: "#f8fafe",
          width: "100%",
          minHeight: "calc(100vh - 64px)",
          position: "relative",
          overflow: "hidden",
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            background: 'radial-gradient(circle at 20% 30%, rgba(48, 146, 227, 0.06) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(58, 150, 251, 0.06) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
          {/* Main Content Card */}
          <Paper
            sx={{
              borderRadius: 0,
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafe 50%, #ffffff 100%)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              border: "none",
              borderBottom: "1px solid rgba(48, 146, 227, 0.15)",
              overflow: "hidden",
              width: "100%",
              minHeight: "calc(100vh - 64px)",
              position: "relative",
            }}
          >
            <Box sx={{ p: { xs: 3, md: 4, lg: 6 }, maxWidth: "1400px", mx: "auto", width: "100%" }}>
              {/* Welcome Message Section */}
              <Box
                sx={{
                  textAlign: "center",
                  mb: 6,
                  py: 5,
                  px: 4,
                  background: "linear-gradient(135deg, rgba(48, 146, 227, 0.08) 0%, rgba(255, 255, 255, 0.95) 50%, rgba(58, 150, 251, 0.05) 100%)",
                  borderRadius: 3,
                  border: "1px solid rgba(48, 146, 227, 0.1)",
                  boxShadow: "0 4px 20px rgba(48, 146, 227, 0.1)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: "linear-gradient(90deg, #3092e3 0%, #3a96fb 100%)",
                  },
                }}
              >
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: "#1976d2",
                    background: "linear-gradient(135deg, #1976d2 0%, #3092e3 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: { xs: "1.75rem", md: "2rem" },
                    lineHeight: 1.5,
                    overflow: "visible",
                    textOverflow: "clip",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    paddingTop: "0.2em",
                    paddingBottom: "0.2em",
                  }}
                >
                  {studentName
                    ? `${t("welcome") || "Welcome"}, ${studentName} ${t("to_school_selection_portal") || "to School Selection Portal"}!`
                    : t("school_selection_welcome_title") || "Welcome to School Selection Portal!"}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0,
                    color: "#666",
                    fontWeight: 400,
                    lineHeight: 2,
                    maxWidth: 800,
                    mx: "auto",
                    fontSize: { xs: "0.9375rem", md: "1rem" },
                    overflow: "visible",
                    textOverflow: "clip",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    paddingTop: "0.1em",
                    paddingBottom: "0.1em",
                  }}
                >
                  {t("school_selection_welcome_message") || "We are excited to guide you through the process. Please select your desired school from the dropdown menu below to get started. If you are a returning applicant, you can update your application."}
                </Typography>
              </Box>

              {/* Header Section */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  mb: 5,
                  pb: 3,
                  borderBottom: "2px solid",
                  borderImage: "linear-gradient(90deg, rgba(48, 146, 227, 0.2) 0%, rgba(48, 146, 227, 0.05) 50%, rgba(48, 146, 227, 0.2) 100%) 1",
                }}
              >
                <Avatar
                  sx={{
                    background: "linear-gradient(135deg, #3092e3 0%, #3a96fb 100%)",
                    width: 64,
                    height: 64,
                    boxShadow: "0 6px 20px rgba(48, 146, 227, 0.3)",
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 36, color: "#fff" }} />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{
                      background: "linear-gradient(135deg, #1976d2 0%, #3092e3 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mb: 0.5,
                      fontSize: "1.75rem",
                    }}
                  >
                    {t("school_selection") || "School Selection"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.9375rem",
                      color: "#666",
                      fontWeight: 500,
                    }}
                  >
                    {t("select_your_school_to_continue") || "Select your school to continue"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleStartApplication}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      px: 4,
                      py: 1.75,
                      background: "linear-gradient(135deg, #3092e3 0%, #3a96fb 100%)",
                      boxShadow: "0 4px 16px rgba(48, 146, 227, 0.3)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "linear-gradient(135deg, #1976d2 0%, #3092e3 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 24px rgba(48, 146, 227, 0.4)",
                      },
                    }}
                  >
                    {t("start_application_form") || "Start Application Form"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleUpdateApplicationSearch}
                    startIcon={<SearchIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      px: 4,
                      py: 1.75,
                      borderColor: "#3092e3",
                      borderWidth: 2,
                      color: "#3092e3",
                      background: "rgba(255, 255, 255, 0.8)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#1976d2",
                        backgroundColor: "rgba(48, 146, 227, 0.1)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 16px rgba(48, 146, 227, 0.2)",
                      },
                    }}
                  >
                    {t("update_application_form") || "Update Application Form"}
                  </Button>
                </Box>
              </Box>

              {/* Schools Grid */}
              {error && (
                <Alert severity="error" sx={{ mb: 4, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                  {error}
                </Alert>
              )}
              {!error && schools.length === 0 && (
                <Alert severity="info" sx={{ mb: 4, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                  No schools available at the moment.
                </Alert>
              )}
              {!error && schools.length > 0 && (
                <Grid container spacing={4}>
                  {schools.map((school) => (
                    <Grid item xs={12} sm={6} md={4} key={school.id}>
                      <Card
                        sx={{
                          height: "100%",
                          borderRadius: 3,
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                          border: "1px solid rgba(48, 146, 227, 0.15)",
                          background: "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background: "linear-gradient(90deg, #3092e3 0%, #3a96fb 50%, #3092e3 100%)",
                            transform: "scaleX(0)",
                            transformOrigin: "left",
                            transition: "transform 0.4s ease",
                          },
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: "0 12px 32px rgba(48, 146, 227, 0.2)",
                            border: "1px solid rgba(48, 146, 227, 0.4)",
                            background: "linear-gradient(180deg, #ffffff 0%, #f0f7ff 100%)",
                            "&::before": {
                              transform: "scaleX(1)",
                            },
                            "& .school-avatar": {
                              transform: "scale(1.1) rotate(5deg)",
                              boxShadow: "0 12px 32px rgba(48, 146, 227, 0.4)",
                            },
                          },
                        }}
                      >
                        <CardContent sx={{ textAlign: "center", p: 4, display: "flex", flexDirection: "column", height: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mb: 3,
                            }}
                          >
                            <Avatar
                              className="school-avatar"
                              sx={{
                                background: "linear-gradient(135deg, #3092e3 0%, #3a96fb 100%)",
                                width: 90,
                                height: 90,
                                boxShadow: "0 8px 24px rgba(48, 146, 227, 0.3)",
                                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                              }}
                            >
                              <SchoolIcon sx={{ fontSize: 48, color: "#fff" }} />
                            </Avatar>
                          </Box>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            gutterBottom
                            sx={{
                              mb: 3,
                              background: "linear-gradient(135deg, #1976d2 0%, #3092e3 100%)",
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              fontSize: "1.5rem",
                              lineHeight: 1.3,
                              transition: "all 0.3s ease",
                            }}
                          >
                            {school.nameofSchool || `School ${school.id}`}
                          </Typography>

                          <Box sx={{ flexGrow: 1, mb: 3 }}>
                            {school.schoolCode && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 1.5,
                                  mb: 1.5,
                                }}
                              >
                                <DashboardIcon
                                  sx={{
                                    fontSize: 18,
                                    color: "#3092e3",
                                    opacity: 0.8,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.875rem",
                                    fontWeight: 400,
                                    color: "#666",
                                  }}
                                >
                                  Code: {school.schoolCode}
                                </Typography>
                              </Box>
                            )}
                            {school.diseCode && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 1.5,
                                }}
                              >
                                <LocationOnIcon
                                  sx={{
                                    fontSize: 18,
                                    color: "#3092e3",
                                    opacity: 0.8,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.875rem",
                                    fontWeight: 400,
                                    color: "#666",
                                  }}
                                >
                                  DISE: {school.diseCode}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          <Button
                            variant="contained"
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSchoolSelect(school);
                            }}
                            sx={{
                              mt: "auto",
                              py: 1.75,
                              background: "linear-gradient(135deg, #3092e3 0%, #3a96fb 100%)",
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "1rem",
                              borderRadius: 2,
                              boxShadow: "0 4px 16px rgba(48, 146, 227, 0.3)",
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              position: "relative",
                              overflow: "hidden",
                              "&::before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: "-100%",
                                width: "100%",
                                height: "100%",
                                background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                                transition: "left 0.5s ease",
                              },
                              "&:hover": {
                                background: "linear-gradient(135deg, #1976d2 0%, #3092e3 100%)",
                                boxShadow: "0 6px 24px rgba(48, 146, 227, 0.4)",
                                transform: "translateY(-2px)",
                                "&::before": {
                                  left: "100%",
                                },
                              },
                              "&:active": {
                                transform: "translateY(0) scale(0.98)",
                              },
                            }}
                          >
                            {t("select_school") || "Select School"}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Select School Dialog */}
      <Dialog open={selectSchoolDialogOpen} onClose={() => setSelectSchoolDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("select_school") || "Select School"}</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>{t("school") || "School"}</InputLabel>
            <Select
              label={t("school") || "School"}
              value={selectedSchoolId ?? ""}
              onChange={(e) => setSelectedSchoolId(Number(e.target.value))}
            >
              {schools.map((school) => (
                <MenuItem key={school.id} value={school.id}>
                  {school.nameofSchool || `School ${school.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectSchoolDialogOpen(false)}>{t("cancel") || "Cancel"}</Button>
          <Button
            variant="contained"
            onClick={handleOpenAdmissionForm}
            disabled={!selectedSchoolId}
          >
            {t("next") || "Next"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Application Search Dialog */}
      <Dialog
        open={updateSearchDialogOpen}
        onClose={() => {
          setUpdateSearchDialogOpen(false);
          setMobileNo("");
          setDob(null);
          setSearchError("");
          setMobileValidationError("");
          setSearchSchoolId(undefined);
          setShouldSearch(false);
          setSearchMobileNo("");
          setSearchDob("");
          setFoundApplication(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">{t("search_application_form") || "Search Application Form"}</Typography>
          <IconButton
            onClick={() => {
              setUpdateSearchDialogOpen(false);
              setMobileNo("");
              setDob(null);
              setSearchError("");
              setMobileValidationError("");
              setSearchSchoolId(undefined);
              setShouldSearch(false);
              setSearchMobileNo("");
              setSearchDob("");
              setFoundApplication(null);
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label={t("select_school") || "Select School"}
                  value={searchSchoolId || ""}
                  onChange={(e) => {
                    const schoolId = e.target.value ? Number(e.target.value) : undefined;
                    setSearchSchoolId(schoolId);
                    setSearchError("");
                  }}
                  size="small"
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t("select_school") || "Select School"}</em>
                  </MenuItem>
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.nameofSchool || `School ${school.id}`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("mobile_number") || "Mobile Number"}
                  value={mobileNo}
                  onChange={(e) => {
                    // Only allow digits and limit to 10 digits
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setMobileNo(value);
                    // Clear errors when user starts typing
                    setSearchError("");
                    setMobileValidationError("");
                  }}
                  placeholder={t("enter_mobile_number") || "Enter mobile number"}
                  inputProps={{ maxLength: 10 }}
                  type="tel"
                  error={!!mobileValidationError || (!!searchError && !mobileNo.trim())}
                  helperText={mobileValidationError || (searchError && !mobileNo.trim() ? searchError : "")}
                />
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t("date_of_birth") || "Date of Birth"}
                    value={dob}
                    onChange={(newValue) => {
                      setDob(newValue);
                      setSearchError("");
                    }}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!searchError && !dob,
                        helperText: searchError && !dob ? searchError : "",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              {searchError && mobileNo && dob && (
                <Grid item xs={12}>
                  <Alert severity="error">{searchError}</Alert>
                </Grid>
              )}
              {shouldSearch && applicationFormsByMobileNoAndDOBQuery.isLoading && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setUpdateSearchDialogOpen(false);
              setMobileNo("");
              setDob(null);
              setSearchError("");
              setMobileValidationError("");
              setSearchSchoolId(undefined);
              setShouldSearch(false);
              setSearchMobileNo("");
              setSearchDob("");
              setFoundApplication(null);
            }}
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleUpdateSearch}
            variant="contained"
            disabled={!searchSchoolId || !mobileNo.trim() || !dob || (shouldSearch && applicationFormsByMobileNoAndDOBQuery.isLoading)}
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            {shouldSearch && applicationFormsByMobileNoAndDOBQuery.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("search") || "Search"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admission Form Modal */}
      <AdmissionFromModal
        open={admissionModalOpen}
        onClose={() => {
          setAdmissionModalOpen(false);
          setIsEditMode(false);
          setCurrentFormData(undefined);
          setFoundApplication(null);
        }}
        onSubmit={handleAdmissionSubmit}
        schoolData={selectedSchool || undefined}
        isEdit={isEditMode}
        initialData={memoizedInitialData}
        availableClasses={classesQuery.data || []}
        availableSessions={sessionsQuery.data || []}
        availableSubjects={subjectsQuery.data || []}
      />
      {submitError && (
        <Alert
          severity="error"
          sx={{
            position: "fixed",
            top: 16,
            right: 16,
            zIndex: 1400,
            minWidth: 300,
            maxWidth: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
          }}
          onClose={() => setSubmitError(null)}
        >
          {submitError}
        </Alert>
      )}
      {submitSuccess && (
        <Alert
          severity="success"
          sx={{
            position: "fixed",
            top: submitError ? 80 : 16,
            right: 16,
            zIndex: 1400,
            minWidth: 300,
            maxWidth: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
          }}
          onClose={() => setSubmitSuccess(null)}
        >
          {submitSuccess}
        </Alert>
      )}

      {/* School Description Modal */}
      <Dialog
        open={schoolDescriptionModalOpen}
        onClose={handleCloseSchoolDescription}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(48, 146, 227, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            pb: 2,
            pt: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 700,
              color: "#1976d2",
              background: "linear-gradient(135deg, #1976d2 0%, #3092e3 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {schoolForDescription?.nameofSchool || `School ${schoolForDescription?.id}`}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 4, py: 3 }}>
          <Typography
            variant="body1"
            sx={{
              color: "#555",
              lineHeight: 1.8,
              fontSize: "1rem",
              textAlign: "justify",
            }}
          >
            {schoolForDescription ? generateSchoolDescription(schoolForDescription) : ""}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, pt: 2, gap: 2 }}>
          <Button
            onClick={handleCloseSchoolDescription}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              borderColor: "#3092e3",
              color: "#3092e3",
              "&:hover": {
                borderColor: "#1976d2",
                backgroundColor: "rgba(48, 146, 227, 0.08)",
              },
            }}
          >
            {t("close") || "Close"}
          </Button>
          <Button
            onClick={handleProceedWithSchool}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              bgcolor: "#3092e3",
              boxShadow: "0 4px 12px rgba(48, 146, 227, 0.3)",
              "&:hover": {
                bgcolor: "#1976d2",
                boxShadow: "0 6px 20px rgba(48, 146, 227, 0.4)",
              },
            }}
          >
            {t("proceed_to_application_form") || "Proceed to Application form"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectionDialogOpen}
        onClose={() => setRejectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(211, 47, 47, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            pb: 2,
            pt: 3,
            backgroundColor: "#ffebee",
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              color: "#d32f2f",
            }}
          >
            {t("your_application_form_is_rejected") || "Your application form is rejected"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3, textAlign: "center" }}>
          <Typography
            variant="body1"
            sx={{
              color: "#555",
              lineHeight: 1.8,
              fontSize: "1rem",
            }}
          >
            {"Your application form has been rejected. Please contact the school administration for more information."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, pt: 2, justifyContent: "center" }}>
          <Button
            onClick={() => setRejectionDialogOpen(false)}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              bgcolor: "#d32f2f",
              boxShadow: "0 4px 12px rgba(211, 47, 47, 0.3)",
              "&:hover": {
                bgcolor: "#c62828",
                boxShadow: "0 6px 20px rgba(211, 47, 47, 0.4)",
              },
            }}
          >
            {t("close") || "Close"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolSelectionDashboard;

